import { useState, useEffect, useMemo } from 'react';
interface MetaTemplateButton {
  type: 'URL' | 'QUICK_REPLY' | 'PHONE_NUMBER';
  text: string;
  url?: string;
  phone_number?: string;
}

interface TemplateComponent {
  type: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';
  format?: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  text?: string;
  example?: Record<string, unknown>;
  buttons?: MetaTemplateButton[];
}

interface Template {
  id: string;
  name: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'DISABLED' | 'PAUSED' | string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION' | string;
  language: string;
  components: TemplateComponent[];
  quality_score?: { score: string; date: number } | null;
}

interface TemplateMapping {
  id?: number;
  variable_index: number;
  source: 'field' | 'static' | 'function';
  value: string;
}

const statusColors: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  APPROVED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700',
  DISABLED: 'bg-gray-100 text-gray-700',
  PAUSED: 'bg-orange-100 text-orange-700',
};

const PRESET_OPTIONS = [
  { label: '推薦碼 (referrer.referralCode)', source: 'field' as const, value: 'referrer.referralCode' },
  { label: 'Dashboard 連結 (buildDashboardLink)', source: 'function' as const, value: 'buildDashboardLink' },
  { label: '推廣連結 (buildReferralLink)', source: 'function' as const, value: 'buildReferralLink' },
  { label: '訂單編號 (booking.token)', source: 'field' as const, value: 'booking.token' },
  { label: '訂單金額 (booking.totalAmount)', source: 'field' as const, value: 'booking.totalAmount' },
  { label: '套餐名稱 (packageBooking.packageName)', source: 'field' as const, value: 'packageBooking.packageName' },
  { label: '朋友電話尾數 (maskPhoneLast4)', source: 'function' as const, value: 'maskPhoneLast4' },
  { label: '回贈金額 (referralOrder.commissionAmount)', source: 'field' as const, value: 'referralOrder.commissionAmount' },
  { label: '官方 WhatsApp 號碼 (getBusinessWhatsAppNumber)', source: 'function' as const, value: 'getBusinessWhatsAppNumber' },
  { label: '自訂文字', source: 'static' as const, value: '' },
];

function extractVariables(text?: string): string[] {
  if (!text) return [];
  const matches = text.match(/\{\{\d+\}\}/g) || [];
  return Array.from(new Set(matches)).sort((a, b) => {
    const na = Number(a.replace(/\D/g, ''));
    const nb = Number(b.replace(/\D/g, ''));
    return na - nb;
  });
}

function componentPreviewText(c: TemplateComponent): string {
  if (c.type === 'BODY') return c.text || '';
  if (c.type === 'HEADER') {
    if (c.format === 'IMAGE') return '[圖片 Header]';
    if (c.format === 'VIDEO') return '[影片 Header]';
    if (c.format === 'DOCUMENT') return '[文件 Header]';
    return c.text || '';
  }
  if (c.type === 'FOOTER') return c.text || '';
  if (c.type === 'BUTTONS') {
    return (c.buttons || []).map((b) => `[${b.type}] ${b.text}`).join(' / ');
  }
  return '';
}

interface WhatsAppTemplatesSectionProps {
  apiFetch: (path: string, init?: RequestInit) => Promise<Response>;
}

export default function WhatsAppTemplatesSection({ apiFetch }: WhatsAppTemplatesSectionProps) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [name, setName] = useState('');
  const [category, setCategory] = useState<'MARKETING' | 'UTILITY' | 'AUTHENTICATION'>('UTILITY');
  const [language, setLanguage] = useState('zh_HK');
  const [headerFormat, setHeaderFormat] = useState<'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT'>('NONE');
  const [headerText, setHeaderText] = useState('');
  const [headerMediaUrl, setHeaderMediaUrl] = useState('');
  const [body, setBody] = useState('');
  const [footer, setFooter] = useState('');
  const [buttons, setButtons] = useState<MetaTemplateButton[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);
  const [mappings, setMappings] = useState<TemplateMapping[]>([]);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingSaving, setMappingSaving] = useState(false);

  const [testPhone, setTestPhone] = useState('');
  const [testVariables, setTestVariables] = useState<Record<string, string>>({});
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  const [channel, setChannel] = useState<'meta' | 'cloudwapi'>('meta');
  const [channelLoading, setChannelLoading] = useState(false);

  const [recreatingKey, setRecreatingKey] = useState<string | null>(null);

  useEffect(() => {
    fetchTemplates();
    fetchChannel();
  }, []);

  useEffect(() => {
    if (previewTemplate) {
      fetchMappings(previewTemplate.name, previewTemplate.language);
      setTestPhone('');
      setTestResult(null);
    }
  }, [previewTemplate]);

  useEffect(() => {
    if (!previewTemplate) return;
    const vars: Record<string, string> = {};
    for (const v of allVariables(previewTemplate)) {
      const idx = v.replace(/\D/g, '');
      const m = getMappingForVariable(Number(idx));
      vars[idx] = m.value;
    }
    setTestVariables(vars);
  }, [previewTemplate, mappings]);

  async function fetchChannel() {
    try {
      const res = await apiFetch('/api/admin/whatsapp/channel');
      const data = await res.json();
      if (res.ok && (data.channel === 'meta' || data.channel === 'cloudwapi')) {
        setChannel(data.channel);
      }
    } catch {
      // ignore
    }
  }

  async function handleChannelChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const newChannel = e.target.value as 'meta' | 'cloudwapi';
    setChannelLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await apiFetch('/api/admin/whatsapp/channel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel: newChannel }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '切換失敗');
      setChannel(newChannel);
      setSuccess(`發送渠道已切換至 ${newChannel === 'meta' ? 'Meta API' : 'CloudWAPI'}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : '切換渠道失敗');
    } finally {
      setChannelLoading(false);
    }
  }

  async function fetchTemplates() {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch('/api/admin/whatsapp/templates');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '載入失敗');
      setTemplates(data.templates || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入模板失敗');
    } finally {
      setLoading(false);
    }
  }

  async function fetchMappings(templateName: string, language: string) {
    setMappingLoading(true);
    try {
      const res = await apiFetch(
        `/api/admin/whatsapp/templates/${encodeURIComponent(templateName)}/${encodeURIComponent(language)}/mappings`
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '載入對應失敗');
      setMappings((data.mappings || []).map((m: TemplateMapping & { variable_index: number }) => ({
        id: m.id,
        variable_index: m.variable_index,
        source: m.source,
        value: m.value,
      })));
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入變數對應失敗');
    } finally {
      setMappingLoading(false);
    }
  }

  function validateTemplateName(value: string) {
    return /^[a-z0-9_]+$/.test(value);
  }

  function buildComponents(): TemplateComponent[] {
    const components: TemplateComponent[] = [];
    if (headerFormat === 'TEXT' && headerText.trim()) {
      components.push({ type: 'HEADER', format: 'TEXT', text: headerText.trim() });
    }
    if ((headerFormat === 'IMAGE' || headerFormat === 'VIDEO' || headerFormat === 'DOCUMENT') && headerMediaUrl.trim()) {
      components.push({
        type: 'HEADER',
        format: headerFormat,
        example: { header_url: [headerMediaUrl.trim()] },
      });
    }
    const bodyText = body.trim();
    const bodyVars = extractVariables(bodyText);
    const bodyComponent: TemplateComponent = { type: 'BODY', text: bodyText };
    if (bodyVars.length > 0) {
      bodyComponent.example = { body_text: [bodyVars.map((_, i) => `範例${i + 1}`)] };
    }
    components.push(bodyComponent);
    if (footer.trim()) components.push({ type: 'FOOTER', text: footer.trim() });
    if (buttons.length > 0) {
      components.push({ type: 'BUTTONS', buttons: buttons.filter((b) => b.text.trim()) });
    }
    return components;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!name || !body) {
      setError('請填寫模板名稱與內文');
      return;
    }

    if (!validateTemplateName(name)) {
      setError('模板名稱只能使用小寫英文、數字與底線');
      return;
    }

    const components = buildComponents();

    setSubmitting(true);
    try {
      const res = await apiFetch('/api/admin/whatsapp/templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, category, language, components }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '建立失敗');

      setSuccess(`模板已提交，Meta 審批狀態：${data.result?.status || 'PENDING'}`);
      setName('');
      setHeaderFormat('NONE');
      setHeaderText('');
      setHeaderMediaUrl('');
      setBody('');
      setFooter('');
      setButtons([]);
      fetchTemplates();
    } catch (err) {
      setError(err instanceof Error ? err.message : '建立模板失敗');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(template: Template) {
    if (!window.confirm(`確定要刪除模板「${template.name} (${template.language})」嗎？`)) return;
    setError('');
    setSuccess('');
    try {
      const res = await apiFetch(
        `/api/admin/whatsapp/templates/${encodeURIComponent(template.name)}/${encodeURIComponent(template.language)}`,
        { method: 'DELETE' }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '刪除失敗');
      setSuccess('模板已刪除');
      fetchTemplates();
    } catch (err) {
      setError(err instanceof Error ? err.message : '刪除模板失敗');
    }
  }

  function addButton(type: MetaTemplateButton['type']) {
    setButtons((prev) => [...prev, { type, text: '', url: '', phone_number: '' }]);
  }

  function updateButton(index: number, patch: Partial<MetaTemplateButton>) {
    setButtons((prev) => prev.map((b, i) => (i === index ? { ...b, ...patch } : b)));
  }

  function removeButton(index: number) {
    setButtons((prev) => prev.filter((_, i) => i !== index));
  }

  function allVariables(template?: Template | null): string[] {
    if (!template) return [];
    const texts: string[] = [];
    for (const c of template.components || []) {
      if (c.type === 'BODY' || (c.type === 'HEADER' && c.format === 'TEXT')) {
        texts.push(c.text || '');
      }
    }
    return extractVariables(texts.join(' '));
  }

  function getMappingForVariable(index: number): TemplateMapping {
    const existing = mappings.find((m) => m.variable_index === index);
    return existing || { variable_index: index, source: 'static', value: '' };
  }

  function updateMapping(index: number, patch: Partial<TemplateMapping>) {
    setMappings((prev) => {
      const existing = prev.find((m) => m.variable_index === index);
      if (existing) {
        return prev.map((m) => (m.variable_index === index ? { ...m, ...patch } : m));
      }
      return [...prev, { variable_index: index, source: 'static', value: '', ...patch }];
    });
  }

  async function saveMappings() {
    if (!previewTemplate) return;
    setError('');
    setSuccess('');
    setMappingSaving(true);
    try {
      const variables = allVariables(previewTemplate);
      const payload = variables.map((_, idx) => {
        const m = getMappingForVariable(idx + 1);
        return { variable_index: idx + 1, source: m.source, value: m.value };
      });
      const res = await apiFetch(
        `/api/admin/whatsapp/templates/${encodeURIComponent(previewTemplate.name)}/${encodeURIComponent(previewTemplate.language)}/mappings`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mappings: payload }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '儲存失敗');
      setSuccess('變數對應已儲存');
      fetchMappings(previewTemplate.name, previewTemplate.language);
    } catch (err) {
      setError(err instanceof Error ? err.message : '儲存變數對應失敗');
    } finally {
      setMappingSaving(false);
    }
  }

  async function handleRecreate(template: Template) {
    const key = `${template.name}:${template.language}`;
    setRecreatingKey(key);
    setError('');
    setSuccess('');
    try {
      const res = await apiFetch(
        `/api/admin/whatsapp/templates/${encodeURIComponent(template.name)}/${encodeURIComponent(template.language)}/recreate`,
        { method: 'POST' }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '重建失敗');
      setSuccess(`模板 ${template.name} 已重新建立，狀態：${data.status || 'PENDING'}`);
      fetchTemplates();
    } catch (err) {
      setError(err instanceof Error ? err.message : '重建模板失敗');
    } finally {
      setRecreatingKey(null);
    }
  }

  async function handleTestSend() {
    if (!previewTemplate) return;
    setTestLoading(true);
    setTestResult(null);
    try {
      const variables: Record<string, string> = {};
      for (const v of allVariables(previewTemplate)) {
        const idx = v.replace(/\D/g, '');
        variables[idx] = testVariables[idx] || '';
      }
      const res = await apiFetch(
        `/api/admin/whatsapp/templates/${encodeURIComponent(previewTemplate.name)}/${encodeURIComponent(previewTemplate.language)}/send`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone: testPhone, variables }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '發送失敗');
      const channelLabel = data.channel === 'cloudwapi' ? 'CloudWAPI' : 'Meta API';
      setTestResult({ ok: true, message: `發送成功（${channelLabel}）Message ID: ${data.messageId || 'N/A'}` });
    } catch (err) {
      setTestResult({ ok: false, message: err instanceof Error ? err.message : '發送失敗' });
    } finally {
      setTestLoading(false);
    }
  }

  const bodyVariables = useMemo(() => extractVariables(body), [body]);

  return (
    <div className="space-y-8">
      {/* Create form */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h3 className="font-bold text-lg text-[#0d1b2a] mb-4">建立新模板</h3>
        {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
        {success && <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-lg text-sm">{success}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">模板名稱</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="referral_promo_v1"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0a4c6b]"
              />
              <p className="text-xs text-gray-400 mt-1">小寫英文、數字、底線</p>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">類別</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as typeof category)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0a4c6b]"
              >
                <option value="UTILITY">Utility（交易/通知）</option>
                <option value="MARKETING">Marketing（推廣）</option>
                <option value="AUTHENTICATION">Authentication（驗證碼）</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">語言</label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0a4c6b]"
              >
                <option value="zh_HK">繁體中文（香港） zh_HK</option>
                <option value="zh_CN">簡體中文 zh_CN</option>
                <option value="en_US">英文 en_US</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">標題格式</label>
              <select
                value={headerFormat}
                onChange={(e) => setHeaderFormat(e.target.value as typeof headerFormat)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0a4c6b]"
              >
                <option value="NONE">無標題</option>
                <option value="TEXT">文字標題</option>
                <option value="IMAGE">圖片標題</option>
                <option value="VIDEO">影片標題</option>
                <option value="DOCUMENT">文件標題</option>
              </select>
            </div>
            <div>
              {headerFormat === 'TEXT' && (
                <>
                  <label className="block text-sm text-gray-600 mb-1">標題文字</label>
                  <input
                    type="text"
                    value={headerText}
                    onChange={(e) => setHeaderText(e.target.value)}
                    placeholder="你的私人海島，正在馬爾代夫等你——"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0a4c6b]"
                  />
                </>
              )}
              {(headerFormat === 'IMAGE' || headerFormat === 'VIDEO' || headerFormat === 'DOCUMENT') && (
                <>
                  <label className="block text-sm text-gray-600 mb-1">
                    {headerFormat === 'IMAGE' ? '標題圖片 URL' : headerFormat === 'VIDEO' ? '標題影片 URL' : '標題文件 URL'}
                  </label>
                  <input
                    type="text"
                    value={headerMediaUrl}
                    onChange={(e) => setHeaderMediaUrl(e.target.value)}
                    placeholder="https://..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0a4c6b]"
                  />
                </>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">內文（Body）</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              placeholder="用我嘅專屬連結預約，不但有專人跟進，仲有額外驚喜安排，立即查看：{{1}}。"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0a4c6b]"
            />
            <p className="text-xs text-gray-400 mt-1">
              用 {'{{1}}'}、{'{{2}}'} 表示變數。目前變數：{bodyVariables.join(', ') || '無'}
            </p>
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">頁尾（Footer，可選）</label>
            <input
              type="text"
              value={footer}
              onChange={(e) => setFooter(e.target.value)}
              placeholder="HK Stay Islands"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0a4c6b]"
            />
            <p className="text-xs text-gray-400 mt-1">Footer 不能有變數</p>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-sm text-gray-600">按鈕（Buttons，可選）</label>
              <div className="space-x-2">
                <button
                  type="button"
                  onClick={() => addButton('URL')}
                  className="px-2 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50"
                >
                  + 網址按鈕
                </button>
                <button
                  type="button"
                  onClick={() => addButton('QUICK_REPLY')}
                  className="px-2 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50"
                >
                  + 快速回覆
                </button>
                <button
                  type="button"
                  onClick={() => addButton('PHONE_NUMBER')}
                  className="px-2 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50"
                >
                  + 電話按鈕
                </button>
              </div>
            </div>
            {buttons.length === 0 && <p className="text-xs text-gray-400">尚未加入按鈕</p>}
            {buttons.map((b, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end border-t border-gray-100 pt-2">
                <div className="md:col-span-2">
                  <span className="text-xs text-gray-500">{b.type}</span>
                </div>
                <div className="md:col-span-4">
                  <input
                    type="text"
                    value={b.text}
                    onChange={(e) => updateButton(i, { text: e.target.value })}
                    placeholder="按鈕文字"
                    className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                  />
                </div>
                {b.type === 'URL' && (
                  <div className="md:col-span-4">
                    <input
                      type="text"
                      value={b.url || ''}
                      onChange={(e) => updateButton(i, { url: e.target.value })}
                      placeholder="https://..."
                      className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                    />
                  </div>
                )}
                {b.type === 'PHONE_NUMBER' && (
                  <div className="md:col-span-4">
                    <input
                      type="text"
                      value={b.phone_number || ''}
                      onChange={(e) => updateButton(i, { phone_number: e.target.value })}
                      placeholder="852xxxxxxxx"
                      className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                    />
                  </div>
                )}
                <div className="md:col-span-2 text-right">
                  <button
                    type="button"
                    onClick={() => removeButton(i)}
                    className="text-red-600 hover:text-red-800 text-xs"
                  >
                    刪除
                  </button>
                </div>
              </div>
            ))}
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="px-5 py-2 bg-[#0a4c6b] text-white rounded-lg text-sm font-medium hover:bg-[#0d1b2a] disabled:opacity-50 transition"
          >
            {submitting ? '提交中...' : '提交 Meta 審批'}
          </button>
        </form>
      </div>

      {/* Template list */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-bold text-lg text-[#0d1b2a]">已建立模板</h3>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">發送渠道</label>
            <select
              value={channel}
              onChange={handleChannelChange}
              disabled={channelLoading}
              className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0a4c6b] disabled:opacity-50"
            >
              <option value="meta">Meta API</option>
              <option value="cloudwapi">CloudWAPI</option>
            </select>
            <button
              onClick={fetchTemplates}
              disabled={loading}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              {loading ? '載入中...' : '重新整理'}
            </button>
          </div>
        </div>

        {templates.length === 0 && !loading ? (
          <p className="text-gray-500 text-sm">尚未建立任何模板</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">名稱</th>
                  <th className="px-4 py-3 text-left font-medium">類別</th>
                  <th className="px-4 py-3 text-left font-medium">語言</th>
                  <th className="px-4 py-3 text-left font-medium">狀態</th>
                  <th className="px-4 py-3 text-left font-medium">預覽</th>
                  <th className="px-4 py-3 text-right font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {templates.map((t) => (
                  <tr key={`${t.name}-${t.language}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono">{t.name}</td>
                    <td className="px-4 py-3">{t.category}</td>
                    <td className="px-4 py-3">{t.language}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${statusColors[t.status] || 'bg-gray-100 text-gray-700'}`}>
                        {t.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-xs truncate text-gray-500">
                      {t.components?.find((c) => c.type === 'BODY')?.text || '-'}
                    </td>
                    <td className="px-4 py-3 text-right space-x-3">
                      <button
                        onClick={() => setPreviewTemplate(t)}
                        className="text-[#0a4c6b] hover:text-[#0d1b2a] text-sm"
                      >
                        查看
                      </button>
                      <button
                        onClick={() => handleRecreate(t)}
                        disabled={recreatingKey === `${t.name}:${t.language}`}
                        className="text-orange-600 hover:text-orange-800 text-sm disabled:opacity-50"
                      >
                        {recreatingKey === `${t.name}:${t.language}` ? '重建中...' : '重建'}
                      </button>
                      <button
                        onClick={() => handleDelete(t)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        刪除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Preview / mapping modal */}
      {previewTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-[#0d1b2a]">
                {previewTemplate.name} <span className="text-sm font-normal text-gray-500">({previewTemplate.language})</span>
              </h3>
              <button
                onClick={() => setPreviewTemplate(null)}
                className="text-gray-500 hover:text-gray-800 text-xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <h4 className="text-sm font-semibold text-gray-700">完整內容</h4>
                {previewTemplate.components?.map((c, idx) => (
                  <div key={idx} className="text-sm">
                    <span className="font-medium text-gray-500">{c.type}:</span>
                    <div className="mt-1 whitespace-pre-wrap text-gray-800">{componentPreviewText(c)}</div>
                  </div>
                ))}
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">變數對應設定</h4>
                {mappingLoading ? (
                  <p className="text-xs text-gray-400">載入中...</p>
                ) : (
                  <div className="space-y-3">
                    {allVariables(previewTemplate).length === 0 && (
                      <p className="text-xs text-gray-400">此模板沒有變數</p>
                    )}
                    {allVariables(previewTemplate).map((v) => {
                      const index = Number(v.replace(/\D/g, ''));
                      const m = getMappingForVariable(index);
                      const selectedPreset = PRESET_OPTIONS.find(
                        (p) => p.source === m.source && p.value === m.value
                      );
                      return (
                        <div key={v} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center border border-gray-100 rounded-lg p-2">
                          <div className="md:col-span-2 font-mono text-sm text-gray-600">{v}</div>
                          <div className="md:col-span-4">
                            <select
                              value={selectedPreset ? `${selectedPreset.source}:${selectedPreset.value}` : 'custom'}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === 'custom') {
                                  updateMapping(index, { source: 'static', value: '' });
                                  return;
                                }
                                const [source, ...rest] = val.split(':');
                                updateMapping(index, { source: source as TemplateMapping['source'], value: rest.join(':') });
                              }}
                              className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                            >
                              <option value="custom">自訂</option>
                              {PRESET_OPTIONS.map((p) => (
                                <option key={`${p.source}:${p.value}`} value={`${p.source}:${p.value}`}>
                                  {p.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="md:col-span-6">
                            <input
                              type="text"
                              value={m.value}
                              onChange={(e) => updateMapping(index, { value: e.target.value })}
                              placeholder={m.source === 'static' ? '固定文字' : m.source === 'function' ? 'functionName' : 'object.field'}
                              className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <button
                  onClick={saveMappings}
                  disabled={mappingSaving || mappingLoading}
                  className="mt-3 px-4 py-2 bg-[#0a4c6b] text-white rounded-lg text-sm hover:bg-[#0d1b2a] disabled:opacity-50"
                >
                  {mappingSaving ? '儲存中...' : '儲存變數對應'}
                </button>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">測試發送</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">接收電話</label>
                    <input
                      type="text"
                      value={testPhone}
                      onChange={(e) => setTestPhone(e.target.value)}
                      placeholder="852xxxxxxxx"
                      className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                    />
                  </div>
                  {allVariables(previewTemplate).length > 0 && (
                    <div className="space-y-2">
                      <label className="block text-xs text-gray-500">變數值（測試用）</label>
                      {allVariables(previewTemplate).map((v) => {
                        const idx = v.replace(/\D/g, '');
                        return (
                          <div key={v} className="flex items-center gap-2">
                            <span className="font-mono text-sm text-gray-600 w-16">{v}</span>
                            <input
                              type="text"
                              value={testVariables[idx] || ''}
                              onChange={(e) => setTestVariables((prev) => ({ ...prev, [idx]: e.target.value }))}
                              placeholder={`輸入 ${v} 測試值`}
                              className="flex-1 px-2 py-1.5 border border-gray-200 rounded text-sm"
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {testResult && (
                    <div
                      className={`text-xs p-2 rounded ${
                        testResult.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                      }`}
                    >
                      {testResult.message}
                    </div>
                  )}
                  <button
                    onClick={handleTestSend}
                    disabled={testLoading || !testPhone.trim()}
                    className="px-4 py-2 bg-[#0a4c6b] text-white rounded-lg text-sm hover:bg-[#0d1b2a] disabled:opacity-50"
                  >
                    {testLoading ? '發送中...' : '發送測試訊息'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
